// Pure date math + tz formatting for the calendar feature. Uses only the
// browser's Date + Intl APIs (no third-party date library — the spec
// is explicit that none is installed and native Date suffices).

/** Roll a Date back to the Monday of its ISO week. UTC-safe. */
export function mondayOfIsoWeek(d: Date): Date {
  // JS getUTCDay(): 0=Sun..6=Sat. ISO weekday: 0=Mon..6=Sun.
  const isoDay = (d.getUTCDay() + 6) % 7;
  const m = new Date(d);
  m.setUTCDate(d.getUTCDate() - isoDay);
  m.setUTCHours(0, 0, 0, 0);
  return m;
}

/** Add N days to a Date (UTC-safe). N may be negative. */
export function addDays(d: Date, days: number): Date {
  const r = new Date(d);
  r.setUTCDate(d.getUTCDate() + days);
  return r;
}

/** ISO 8601 week number (1..53). Standard Monday-of-week-1 algorithm. */
export function isoWeekNumber(d: Date): number {
  // Find the Monday of ISO week 1 for d's year.
  const jan4 = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  const isoDay4 = (jan4.getUTCDay() + 6) % 7; // ISO weekday of Jan 4
  const week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - isoDay4);

  // Find the Monday of d's week.
  const isoDayD = (d.getUTCDay() + 6) % 7;
  const dMonday = new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()),
  );
  dMonday.setUTCDate(dMonday.getUTCDate() - isoDayD);

  const weekNum =
    Math.round(
      (dMonday.getTime() - week1Monday.getTime()) / (7 * 24 * 60 * 60 * 1000),
    ) + 1;

  // Before week 1 → belongs to the previous year's last week.
  if (weekNum < 1) {
    return isoWeekNumber(new Date(Date.UTC(d.getUTCFullYear() - 1, 11, 28)));
  }
  // At or after next year's week 1 → week 1 of next year.
  const nextJan4 = new Date(Date.UTC(d.getUTCFullYear() + 1, 0, 4));
  const nextIsoDay4 = (nextJan4.getUTCDay() + 6) % 7;
  const nextWeek1Monday = new Date(nextJan4);
  nextWeek1Monday.setUTCDate(nextJan4.getUTCDate() - nextIsoDay4);
  if (dMonday.getTime() >= nextWeek1Monday.getTime()) return 1;

  return weekNum;
}

/**
 * Returns the calendar date (YYYY-MM-DD) that `instant` falls on in the
 * given IANA timezone. The week grid, "today" comparison, and agenda
 * grouping all need to follow the user's selected tz — not UTC — or
 * users far from UTC see the wrong week around midnight.
 *
 * NOTE: This intentionally uses "en-CA" — that locale produces
 * yyyy-mm-dd which sorts lexically AND is always Western-Arabic digits.
 * The string is NEVER user-facing; it is a stable map key for
 * grouping events by day. Do not localize it.
 */
export function ymdInTz(instant: Date, tz: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(instant);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/**
 * Format an ISO instant in the given IANA timezone. Returns the empty
 * string when the input is null (intentional for all-day events).
 */
export function formatInTz(
  iso: string | null,
  tz: string,
  opts: Intl.DateTimeFormatOptions,
): string {
  if (!iso) return "";
  return new Intl.DateTimeFormat([], { ...opts, timeZone: tz }).format(
    new Date(iso),
  );
}

/** "Today" if d is today's calendar date; otherwise "Tue 19 May". */
export function dayHeading(
  d: Date,
  today: Date,
  locale: string,
  todayLabel: string,
): string {
  const sameDay =
    d.getUTCFullYear() === today.getUTCFullYear() &&
    d.getUTCMonth() === today.getUTCMonth() &&
    d.getUTCDate() === today.getUTCDate();
  const weekday = new Intl.DateTimeFormat(locale, {
    weekday: "short",
    timeZone: "UTC",
  }).format(d);
  const day = d.getUTCDate();
  const month = new Intl.DateTimeFormat(locale, {
    month: "short",
    timeZone: "UTC",
  }).format(d);
  const label = `${weekday} ${day} ${month}`;
  return sameDay ? `${todayLabel} · ${label}` : label;
}

/** Locale-aware short weekday name ("Mon" / "Pzt" / "الإثنين"). */
export function formatWeekday(d: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" }).format(d);
}

/** Locale-aware long month name ("May" / "Mayıs" / "مايو"). */
export function formatMonthLong(d: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, { month: "long", timeZone: "UTC" }).format(d);
}

interface HasImportance {
  importance: "low" | "medium" | "high" | "none";
}

/** Count events grouped by importance bucket. */
export function severityCount<T extends HasImportance>(events: T[]): {
  high: number;
  medium: number;
  low: number;
  none: number;
} {
  const out = { high: 0, medium: 0, low: 0, none: 0 };
  for (const ev of events) out[ev.importance] += 1;
  return out;
}
